import React, { useState, useEffect } from 'react';
import GeneralIcon from '../../../../../assets';
import discountService from '../../../../../services/discountService';
import categoryService from '../../../../../services/categoryService';
import productService from '../../../../../services/productService';
import { buildDiscountPayload, validateDiscountForm } from '../../../../../models/discountModel';

export default function AdicionarDesconto({ discount, onClose, onSave }) {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: 'Promoção',
    value: '',
    code: '',
    finalDate: '',
    active: true,
    applyTo: 'Tipos de Item',
    affectedItems: [],
    maxUses: '',
    personalizedCode: '',
  });
  const [formErrors, setFormErrors] = useState({});
  const [duplicateError, setDuplicateError] = useState('');
  const [useDateCriteria, setUseDateCriteria] = useState(false);
  const [useMaxUses, setUseMaxUses] = useState(false);

  // Step 2 - Application
  const [searchQuery, setSearchQuery] = useState('');
  const [availableItems, setAvailableItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  // Load discount data if editing
  useEffect(() => {
    if (discount) {
      setFormData({
        name: discount.name || '',
        description: discount.description || '',
        type: discount.type || 'Promoção',
        value: discount.value || '',
        code: discount.code || '',
        finalDate: discount.finalDate || '',
        active: discount.active !== false,
        applyTo: discount.applyTo || 'Tipos de Item',
        affectedItems: discount.affectedItems || [],
        maxUses: discount.maxUses || '',
        personalizedCode: discount.personalizedCode || '',
      });
      setUseDateCriteria(!!discount.finalDate);
      setUseMaxUses(!!discount.maxUses);
    }
  }, [discount]);

  // Load available items based on applyTo selection
  useEffect(() => {
    async function loadItems() {
      try {
        let items = [];
        if (formData.applyTo === 'Tipos de Item') {
          items = [
            { id: 'mercadoria', name: 'Mercadoria' },
            { id: 'servico', name: 'Serviço' },
            { id: 'digital', name: 'Digital' },
          ];
        } else if (formData.applyTo === 'Categorias') {
          const categories = await categoryService.fetchCategories('Mercadoria');
          items = categories.map(c => ({ id: c.id, name: c.name }));
        } else if (formData.applyTo === 'Produtos') {
          const result = await productService.searchProducts({ limit: 100 });
          items = (result.items || []).map(p => ({ id: p.id, name: p.title }));
        }
        setAvailableItems(items);
        setFilteredItems(items);
      } catch (err) {
        console.error('Error loading items:', err);
        setAvailableItems([]);
        setFilteredItems([]);
      }
    }
    
    if (step === 2) {
      loadItems();
    }
  }, [step, formData.applyTo]);

  // Filter items based on search
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredItems(availableItems);
    } else {
      const query = searchQuery.toLowerCase();
      setFilteredItems(availableItems.filter(item =>
        item.name.toLowerCase().includes(query)
      ));
    }
    setCurrentPage(1);
  }, [searchQuery, availableItems]);

  // Pagination
  const totalPages = Math.ceil(filteredItems.length / itemsPerPage);
  const paginatedItems = filteredItems.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  function toggleItem(itemId) {
    setFormData(prev => {
      const affected = prev.affectedItems || [];
      const exists = affected.includes(itemId);
      return {
        ...prev,
        affectedItems: exists
          ? affected.filter(id => id !== itemId)
          : [...affected, itemId],
      };
    });
  }

  function isItemSelected(itemId) {
    return (formData.affectedItems || []).includes(itemId);
  }

  function getItemName(itemId) {
    const item = availableItems.find(i => i.id === itemId);
    return item ? item.name : itemId;
  }

  function handleNext() {
    if (step === 1) {
      const validation = validateDiscountForm(formData);
      if (!validation.isValid) {
        setFormErrors(validation.errors);
        return;
      }
      setFormErrors({});
      setStep(2);
    }
  }

  function handlePrevious() {
    if (step === 2) {
      setStep(1);
    } else {
      onClose && onClose();
    }
  }

  async function handleSave() {
    setDuplicateError('');

    const payload = {
      ...buildDiscountPayload(formData),
      applyTo: formData.applyTo,
      affectedItems: formData.affectedItems,
      maxUses: useMaxUses ? formData.maxUses : null,
      personalizedCode: formData.personalizedCode,
    };

    try {
      let result;
      if (discount) {
        result = await discountService.updateDiscount(discount.id, payload);
      } else {
        result = await discountService.createDiscount(payload);
      }

      if (result.error) {
        setDuplicateError(result.error);
        return;
      }

      onSave && onSave();
      onClose && onClose();
    } catch (err) {
      console.error('Error saving discount:', err);
      setDuplicateError('Erro ao salvar desconto');
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg w-full max-w-md max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center gap-3">
          <button
            onClick={handlePrevious}
            className="p-2 -ml-2 rounded hover:bg-gray-100 transition-colors"
          >
            ←
          </button>
          <h3 className="text-lg font-semibold">
            {discount ? 'Editar desconto' : 'Adicionar desconto'}
          </h3>
        </div>

        <div className="p-6">
          {step === 1 ? (
            // ========== STEP 1: Type + About + Usage ==========
            <div className="space-y-6">
              {/* Type selector */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Escolha um tipo de desconto
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, type: 'Promoção' })}
                    className={`p-4 border-2 rounded-lg flex flex-col items-center gap-2 transition-all ${
                      formData.type === 'Promoção'
                        ? 'border-purple-600 bg-purple-50'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    <div className="w-10 h-10 flex items-center justify-center">
                      <GeneralIcon iconName="promocao" className="w-8 h-8 text-purple-600" />
                    </div>
                    <span className="text-sm font-medium">Promoção</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, type: 'Cupom' })}
                    className={`p-4 border-2 rounded-lg flex flex-col items-center gap-2 transition-all ${
                      formData.type === 'Cupom'
                        ? 'border-purple-600 bg-purple-50'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    <div className="w-10 h-10 flex items-center justify-center">
                      <GeneralIcon iconName="cupom" className="w-8 h-8 text-purple-600" />
                    </div>
                    <span className="text-sm font-medium">Cupom</span>
                  </button>
                </div>
              </div>

              {/* About section */}
              <div>
                <h4 className="text-sm font-semibold text-gray-800 mb-3">Sobre</h4>
                
                {/* Name */}
                <div className="mb-3">
                  <label className="block text-xs text-gray-600 mb-1">
                    Título do desconto*
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={e => setFormData({ ...formData, name: e.target.value })}
                    className={`w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:outline-none ${
                      formErrors.name ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder=""
                  />
                  {formErrors.name && (
                    <div className="text-red-500 text-xs mt-1">{formErrors.name}</div>
                  )}
                </div>

                {/* Description */}
                <div>
                  <label className="block text-xs text-gray-600 mb-1">
                    Descrição*
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={e => setFormData({ ...formData, description: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:outline-none resize-none"
                    placeholder="Para você aproveitar a primavera"
                    rows={3}
                  />
                </div>
              </div>

              {/* Usage section - Different for Promoção and Cupom */}
              <div>
                <h4 className="text-sm font-semibold text-gray-800 mb-3">Uso</h4>
                
                {formData.type === 'Cupom' ? (
                  // Cupom fields
                  <>
                    {/* Personalized Code */}
                    <div className="mb-3">
                      <label className="block text-xs text-gray-600 mb-1">
                        Código personalizado*
                      </label>
                      <input
                        type="text"
                        value={formData.personalizedCode}
                        onChange={e => setFormData({ ...formData, personalizedCode: e.target.value.toUpperCase() })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:outline-none font-mono"
                        placeholder=""
                      />
                    </div>

                    {/* Value */}
                    <div className="mb-3">
                      <label className="block text-xs text-gray-600 mb-1">
                        Valor*
                      </label>
                      <input
                        type="number"
                        value={formData.value}
                        onChange={e => setFormData({ ...formData, value: e.target.value })}
                        className={`w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:outline-none ${
                          formErrors.value ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder=""
                        min="0"
                      />
                      {formErrors.value && (
                        <div className="text-red-500 text-xs mt-1">{formErrors.value}</div>
                      )}
                    </div>

                    {/* Final Date */}
                    <div className="mb-3">
                      <label className="block text-xs text-gray-600 mb-1">
                        Data de finalização
                      </label>
                      <input
                        type="date"
                        value={formData.finalDate}
                        onChange={e => setFormData({ ...formData, finalDate: e.target.value })}
                        disabled={!useDateCriteria}
                        className={`w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:outline-none ${
                          !useDateCriteria ? 'bg-gray-50 text-gray-400' : ''
                        }`}
                      />
                    </div>

                    <div className="mb-3">
                      <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={!useDateCriteria}
                          onChange={e => setUseDateCriteria(!e.target.checked)}
                          className="mt-0.5 rounded border-gray-300"
                        />
                        <span>Não utilizar este critério</span>
                      </label>
                    </div>

                    {/* Max Uses */}
                    <div className="mb-3">
                      <label className="block text-xs text-gray-600 mb-1">
                        Máximo de utilizações
                      </label>
                      <input
                        type="number"
                        value={formData.maxUses}
                        onChange={e => setFormData({ ...formData, maxUses: e.target.value })}
                        disabled={!useMaxUses}
                        className={`w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:outline-none ${
                          !useMaxUses ? 'bg-gray-50 text-gray-400' : ''
                        }`}
                        placeholder="1"
                        min="1"
                      />
                    </div>

                    <div>
                      <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={!useMaxUses}
                          onChange={e => setUseMaxUses(!e.target.checked)}
                          className="mt-0.5 rounded border-gray-300"
                        />
                        <span>Não utilizar este critério</span>
                      </label>
                    </div>
                  </>
                ) : (
                  // Promoção fields
                  <>
                    {/* Value */}
                    <div className="mb-3">
                      <label className="block text-xs text-gray-600 mb-1">
                        Valor*
                      </label>
                      <input
                        type="number"
                        value={formData.value}
                        onChange={e => setFormData({ ...formData, value: e.target.value })}
                        className={`w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:outline-none ${
                          formErrors.value ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder=""
                        min="0"
                      />
                      {formErrors.value && (
                        <div className="text-red-500 text-xs mt-1">{formErrors.value}</div>
                      )}
                    </div>

                    {/* Final Date */}
                    <div className="mb-3">
                      <label className="block text-xs text-gray-600 mb-1">
                        Data de finalização
                      </label>
                      <input
                        type="date"
                        value={formData.finalDate}
                        onChange={e => setFormData({ ...formData, finalDate: e.target.value })}
                        disabled={!useDateCriteria}
                        className={`w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:outline-none ${
                          !useDateCriteria ? 'bg-gray-50 text-gray-400' : ''
                        }`}
                      />
                    </div>

                    <div>
                      <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={!useDateCriteria}
                          onChange={e => setUseDateCriteria(!e.target.checked)}
                          className="mt-0.5 rounded border-gray-300"
                        />
                        <span>Não utilizar este critério</span>
                      </label>
                    </div>
                  </>
                )}
              </div>

              {duplicateError && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm">
                  {duplicateError}
                </div>
              )}

              {/* Navigation - Step 1 */}
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 py-2.5 border border-gray-300 text-gray-700 rounded-lg text-sm hover:bg-gray-50 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleNext}
                  className="flex-1 py-2.5 bg-purple-600 text-white rounded-lg text-sm hover:bg-purple-700 transition-colors"
                >
                  Próximo
                </button>
              </div>
            </div>
          ) : (
            // ========== STEP 2: Application ==========
            <div className="space-y-6">
              <div>
                <h4 className="text-sm font-semibold text-gray-800 mb-3">Aplicação</h4>
                
                {/* Type selector dropdown */}
                <div className="mb-3">
                  <label className="block text-xs text-gray-600 mb-1">
                    Tipo de alvo*
                  </label>
                  <select
                    value={formData.applyTo}
                    onChange={e => {
                      setFormData({ ...formData, applyTo: e.target.value, affectedItems: [] });
                      setSearchQuery('');
                      setCurrentPage(1);
                    }}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:outline-none"
                  >
                    <option value="Tipos de Item">Tipos de Item</option>
                    <option value="Categorias">Categorias</option>
                    <option value="Produtos">Produtos</option>
                  </select>
                </div>

                {/* Search */}
                <div className="mb-3">
                  <label className="block text-xs text-gray-600 mb-1">
                    Alvos*
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={e => setSearchQuery(e.target.value)}
                      placeholder={`Pesquisar por ${formData.applyTo.toLowerCase()}`}
                      className="w-full border border-gray-300 rounded-lg pl-3 pr-10 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:outline-none"
                    />
                    <button className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 bg-purple-600 text-white rounded hover:bg-purple-700 transition-colors">
                      <GeneralIcon iconName="search" className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Selected items chips */}
                {formData.affectedItems.length > 0 && (
                  <div className="mb-3 flex flex-wrap gap-2">
                    {formData.affectedItems.map(itemId => (
                      <span
                        key={itemId}
                        className="inline-flex items-center gap-1 bg-purple-100 text-purple-700 px-2 py-1 rounded text-xs"
                      >
                        {getItemName(itemId)}
                        <button
                          onClick={() => toggleItem(itemId)}
                          className="hover:text-purple-900"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}

                {/* Items list */}
                <div className="space-y-2 mb-4">
                  {paginatedItems.length === 0 ? (
                    <div className="text-center py-8 text-sm text-gray-400">
                      Nenhum item encontrado
                    </div>
                  ) : (
                    paginatedItems.map(item => {
                      const selected = isItemSelected(item.id);
                      return (
                        <div
                          key={item.id}
                          className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                          <span className="text-sm text-gray-800">{item.name}</span>
                          <button
                            type="button"
                            onClick={() => toggleItem(item.id)}
                            className={`px-3 py-1 rounded text-xs transition-colors ${
                              selected
                                ? 'bg-red-100 text-red-600 hover:bg-red-200'
                                : 'bg-purple-100 text-purple-600 hover:bg-purple-200'
                            }`}
                          >
                            {selected ? 'Remover' : 'Aplicar'}
                          </button>
                        </div>
                      );
                    })
                  )}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-2">
                    <button
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="px-3 py-1.5 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                    >
                      Anterior
                    </button>
                    <span className="text-sm text-gray-600">
                      Página {currentPage} de {totalPages}
                    </span>
                    <button
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className="px-3 py-1.5 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                    >
                      Próximo
                    </button>
                  </div>
                )}
              </div>

              {duplicateError && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm">
                  {duplicateError}
                </div>
              )}

              {/* Navigation - Step 2 */}
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="flex-1 py-2.5 border border-gray-300 text-gray-700 rounded-lg text-sm hover:bg-gray-50 transition-colors"
                >
                  Anterior
                </button>
                <button
                  type="button"
                  className="flex-1 py-2.5 border border-purple-600 text-purple-600 rounded-lg text-sm hover:bg-purple-50 transition-colors"
                >
                  Página 1
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  className="flex-1 py-2.5 bg-purple-600 text-white rounded-lg text-sm hover:bg-purple-700 transition-colors"
                >
                  Adicionar
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
